// THE LIST FRAME OF A BUILT-IN VIEW — the read-only frame the team, the
// notifications, the reminders, the calls, a call's transcript, a chat
// channel, the mail and a note mount in an AI answer and on a Fly page
// (functions/assistantAppViews.js, the views contract; scope { params,
// embedded }). One frame, so every view reads, waits, says "nothing" and
// cuts the same way:
//
//   MeetRoomsAnswerView: {
//     extends: 'AssistantViewList',
//     viewLoad: 'meetRoomsAnswerLoad',    async (el, params) → { data, failed }
//     viewModel: 'meetRoomsAnswerModel'   (data, params, failed) → model | null
//   }
//
// The read lands in the frame's OWN state (`viewData`, never a root list),
// once per params (functions/assistantViewList.js). The model is
//   { lead, groups: [{ key, groupLabel, groupRows }], body, note, more }
//   lead   one plain paragraph before the rows (a call's summary)
//   groups labelled lists of the home widget kit's rows (HomeWidgetRow) — a
//          row opens the item where it lives
//   body   markdown after the rows (a note's words)
//   note   the one sentence when there is nothing to show (a bundle key)
//   more   { shown, total } or { shown, open: true } — a cut list says so
// The model is derived on every read, so a view follows the root data it
// reads (who is online, a live call, the language). No write control: an
// answer shows the app read-only, so `embedded` changes nothing here.
//
// EMPTY (the views contract): the read answered and the frame shows only its
// one sentence — a Fly page gives the card one row. null while reading.
export const AssistantViewList = {
  flow: 'y',
  gap: 'A',
  flex: '1',
  minWidth: '0',
  minHeight: '0',
  // A long list scrolls inside its card, never past it (a Fly card's grid row
  // has a fixed height).
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  state: { viewData: null, viewFailed: false },
  viewLoad: '',
  viewModel: '',
  empty: (el, s) => el.call('assistantViewListEmpty', el, s),
  onRender: (el) => el.call('assistantViewListLoad', el),

  ViewSkeleton: {
    flow: 'y',
    gap: 'Y',
    ariaBusy: 'true',
    hide: (el, s) => el.call('assistantViewListModel', el.parent, s) !== null,
    Skeleton_1: { height: 'B', round: 'radiusControl' },
    Skeleton_2: { height: 'B', round: 'radiusControl' },
    Skeleton_3: { height: 'B', round: 'radiusControl', width: '60%' }
  },

  ViewNote: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      return !m || !m.note
    },
    text: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      return m && m.note ? String(el.call('polyglot', m.note)) : ''
    }
  },

  ViewLead: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z1',
    lineHeight: '1.6',
    color: 'title',
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
    hide: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      return !m || !m.lead
    },
    text: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      return (m && m.lead) || ''
    }
  },

  ViewGroups: {
    flow: 'y',
    gap: 'A',
    minWidth: '0',
    hide: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      return !m || !m.groups.length
    },
    childExtends: 'AssistantViewGroup',
    childrenAs: 'state',
    children: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      return m ? m.groups : []
    }
  },

  // Markdown words (a note): the shared renderer's children, re-derived with
  // the model — a reactive `text` factory would fight MarkdownPreview's own
  // imperative render.
  ViewBody: {
    flow: 'y',
    gap: 'Z',
    minWidth: '0',
    fontSize: 'Z1',
    lineHeight: '1.6',
    color: 'paragraph',
    overflowWrap: 'anywhere',
    hide: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      return !m || !m.body
    },
    children: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      return m && m.body ? el.call('markdownToChildren', m.body, { demoteHeadings: true }) : []
    }
  },

  // The first N of more: says so, never a silent cut.
  ViewMore: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      return !m || !m.more
    },
    text: (el, s) => {
      const m = el.call('assistantViewListModel', el.parent, s)
      if (!m || !m.more) return ''
      // A known total says "N of M"; a list with a next page and no total
      // says the rest is in the app.
      if (m.more.total == null) {
        return String(el.call('polyglot', 'workspace.answer.moreOpen')).replace(
          '{n}',
          String(m.more.shown)
        )
      }
      return String(el.call('polyglot', 'workspace.answer.more'))
        .replace('{n}', String(m.more.shown))
        .replace('{total}', String(m.more.total))
    }
  }
}

// One labelled list of a view: the house micro-label over the home widget
// kit's rows. A group with no label shows its rows alone.
export const AssistantViewGroup = {
  tag: 'section',
  flow: 'y',
  gap: 'Y',
  minWidth: '0',
  ariaLabel: (el, s) => (s && s.groupLabel) || null,
  AppMicroLabel: {
    hide: (el, s) => !(s && s.groupLabel),
    text: (el, s) => (s && s.groupLabel) || ''
  },
  AssistantViewRows: {
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    childExtends: 'HomeWidgetRow',
    childrenAs: 'state',
    children: (el, s) => (s && Array.isArray(s.groupRows) ? s.groupRows : [])
  }
}
